import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { GlossaryNodeView } from './glossary-node-view'
import { nanoid } from 'nanoid'

export interface GlossaryAttributes {
  id?: string
  term?: string
  definition?: string
}


export const GlossarySpec = {
  group: 'inline',
  inline: true,
  content: 'text*',
  draggable: false,
  attrs: {
    id: { default: nanoid(10) },
    term: { default: '' },
    definition: { default: '' },
  },
  toDOM() { return ['span', { 'data-glossary': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'span[data-glossary]' }],
};


declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    glossary: {
      /**
       * Insert a glossary term
       */
      insertGlossary: (attributes?: GlossaryAttributes) => ReturnType
      /**
       * Update glossary attributes
       */
      updateGlossary: (attributes: Partial<GlossaryAttributes>) => ReturnType
    }
  }
}

export const GlossaryExtension = Node.create({
  name: 'Glossary',

  group: 'inline',

  inline: true,

  atom: true,

  draggable: false,

  addAttributes() {
    return {
      term: {
        default: '',
        parseHTML: element => element.getAttribute('data-term'),
        renderHTML: attributes => {
          if (!attributes.term) {
            return {}
          }
          return {
            'data-term': attributes.term,
          }
        },
      },
      definition: {
        default: '',
        parseHTML: element => element.getAttribute('data-definition'),
        renderHTML: attributes => {
          if (!attributes.definition) {
            return {}
          }
          return {
            'data-definition': attributes.definition,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="Glossary"]',
      },
      {
        tag: 'span[data-glossary="true"]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'strong',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'Glossary',
        'data-glossary': 'true',
        'class': 'font-bold'
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(GlossaryNodeView)
  },

  addCommands() {
    return {
      insertGlossary:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              term: '',
              definition: '',
              ...attributes,
            },
            content: [
              {
                type: 'text',
                text: attributes.term || 'Term',
              },
            ],
          })
        },
      updateGlossary:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },
}) 